import { FormEvent, useEffect, useMemo, useState } from "react";
import { fetchSlotBusyVehicles } from "../../api/appointments";
import { ApiError } from "../../api/client";
import { useDirectoryBundle } from "../../api/directory";
import { UI_LOADING } from "../../constants/uiLabels";
import { formatVehicleOptionLabel } from "./WeeklyCalendar";
import { buildIsoDateTime, type AppointmentDragDropTarget } from "./appointmentDrag";
import "../lesson-reports/LessonReportModal.css";
import "./AppointmentDragVehicleModal.css";

type Props = {
  target: AppointmentDragDropTarget;
  message: string;
  onClose: () => void;
  onConfirm: (vehicleId: string) => Promise<void>;
};

export function AppointmentDragVehicleModal({ target, message, onClose, onConfirm }: Props) {
  const directory = useDirectoryBundle();
  const [vehicleId, setVehicleId] = useState("");
  const [busyIds, setBusyIds] = useState<Set<string>>(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startsAt = useMemo(
    () => buildIsoDateTime(target.date, target.startHHmm),
    [target.date, target.startHHmm],
  );
  const endsAt = useMemo(
    () => buildIsoDateTime(target.date, target.endHHmm),
    [target.date, target.endHHmm],
  );

  useEffect(() => {
    let cancelled = false;
    fetchSlotBusyVehicles({
      startsAt,
      endsAt,
      excludeAppointmentId: target.mode === "move" ? target.source.appointmentId : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        setBusyIds(new Set((res.busy_vehicles || []).map((v) => v.vehicle_id)));
      })
      .catch(() => {
        if (!cancelled) setBusyIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [startsAt, endsAt, target.mode, target.source.appointmentId]);

  const vehicles = directory.data?.vehicles ?? [];
  const available = vehicles.filter((v) => !busyIds.has(v.id));

  useEffect(() => {
    if (!vehicleId && available.length > 0) {
      const preferred = available.find((v) => v.id === target.source.vehicleId);
      setVehicleId(preferred?.id || available[0].id);
    }
  }, [available, target.source.vehicleId, vehicleId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!vehicleId) {
      setError("Müsait bir araç seçin.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(vehicleId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "İşlem tamamlanamadı.");
    } finally {
      setSubmitting(false);
    }
  };

  const modeLabel = target.mode === "copy" ? "Kopyala" : "Taşı";

  return (
    <div
      className="report-modal-backdrop"
      role="presentation"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="report-modal kp-drag-vehicle-modal"
        role="dialog"
        aria-labelledby="drag-vehicle-title"
        aria-modal="true"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="report-modal__header">
          <div>
            <h3 id="drag-vehicle-title">Araç Seç · {modeLabel}</h3>
            <p className="report-modal__sub">
              {target.source.studentName || "Kursiyer"} → {target.instructorName} · {target.date}{" "}
              {target.startHHmm}–{target.endHHmm}
            </p>
          </div>
          <button type="button" className="report-modal__close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </header>

        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="kp-drag-vehicle-modal__body">
            <p className="kp-drag-vehicle-modal__msg">{message}</p>
            {directory.loading && <p>{UI_LOADING}</p>}
            {directory.error && <p className="kp-drag-vehicle-modal__error">{directory.error}</p>}
            {!directory.loading && available.length === 0 && (
              <p className="kp-drag-vehicle-modal__error">Bu saatte müsait araç yok.</p>
            )}
            {available.length > 0 && (
              <label>
                Müsait Araç
                <select
                  required
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  disabled={submitting}
                >
                  {available.map((v) => (
                    <option key={v.id} value={v.id}>
                      {formatVehicleOptionLabel(v.plate_no, v.brand_model, v.gear_type)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {error && <p className="kp-drag-vehicle-modal__error">{error}</p>}
          </div>
          <footer className="kp-drag-vehicle-modal__footer">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              Vazgeç
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn--primary"
              disabled={submitting || available.length === 0}
            >
              {submitting ? "Kaydediliyor…" : modeLabel}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
